// ============================================================================
// Generic aggregation helpers — pure functions, no fetching.
// Dates are local YYYY-MM-DD strings throughout (same convention as
// utils/dateUtils); never construct Date objects from bare date strings
// without a time component.
// ============================================================================

import { addDays, daysBetween, getMonthName } from '../dateUtils';
import { Granularity } from './types';

/** Monday of the week containing the given date. */
export function weekStart(date: string): string {
  const d = new Date(date + 'T12:00:00');
  const day = d.getDay(); // 0 = Sunday
  const offset = day === 0 ? -6 : 1 - day;
  return addDays(date, offset);
}

/** Bucket key for a date at the given granularity. */
export function bucketKey(date: string, granularity: Granularity): string {
  switch (granularity) {
    case 'day':
      return date;
    case 'week':
      return weekStart(date);
    case 'month':
      return date.slice(0, 7); // YYYY-MM
    case 'year':
      return date.slice(0, 4); // YYYY
  }
}

/** Human label for a bucket key. */
export function bucketLabel(key: string, granularity: Granularity): string {
  switch (granularity) {
    case 'day':
    case 'week': {
      const d = new Date(key + 'T12:00:00');
      return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
    case 'month':
      return getMonthName(key + '-01') + ' ' + key.slice(0, 4);
    case 'year':
      return key;
  }
}

/** All bucket keys covering [from, to], inclusive, in order. */
export function enumerateBuckets(from: string, to: string, granularity: Granularity): string[] {
  const keys: string[] = [];
  let cursor = granularity === 'week' ? weekStart(from) : from;
  while (cursor <= to) {
    const key = bucketKey(cursor, granularity);
    if (keys.length === 0 || keys[keys.length - 1] !== key) keys.push(key);
    switch (granularity) {
      case 'day':
        cursor = addDays(cursor, 1);
        break;
      case 'week':
        cursor = addDays(cursor, 7);
        break;
      case 'month': {
        const [y, m] = cursor.split('-').map(Number);
        cursor = m === 12 ? `${y + 1}-01-01` : `${y}-${String(m + 1).padStart(2, '0')}-01`;
        break;
      }
      case 'year':
        cursor = `${Number(cursor.slice(0, 4)) + 1}-01-01`;
        break;
    }
  }
  return keys;
}

/** All day strings in [from, to], inclusive. */
export function enumerateDays(from: string, to: string): string[] {
  const days: string[] = [];
  const count = daysBetween(from, to);
  for (let i = 0; i <= count; i++) days.push(addDays(from, i));
  return days;
}

export function groupBy<T, K extends string | number>(items: T[], key: (item: T) => K): Map<K, T[]> {
  const map = new Map<K, T[]>();
  for (const item of items) {
    const k = key(item);
    const bucket = map.get(k);
    if (bucket) bucket.push(item);
    else map.set(k, [item]);
  }
  return map;
}

export function sumBy<T>(items: T[], value: (item: T) => number): number {
  return items.reduce((acc, item) => acc + value(item), 0);
}

/** Interpolated percentile (p in [0, 100]) of an unsorted numeric array. */
export function percentile(values: number[], p: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const rank = (p / 100) * (sorted.length - 1);
  const lo = Math.floor(rank);
  const hi = Math.ceil(rank);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (rank - lo);
}

export function median(values: number[]): number {
  return percentile(values, 50);
}
